import { useRouter } from "solito/router";
import { v4 as uuid } from "uuid";

import { HapticsService } from "@medbillai/ui";

import {
  GET_HELP_PROMPT_CASE_TYPE_MAP,
  type GetHelpPromptKey,
} from "@medbillai/schemas";
import { issuePreviewUrl } from "@medbillai/utils";

import {
  createDocumentUploadEvent,
  createSystemMessageEvent,
} from "../screens/case/utils/createPreviewEvents";
import { analyticsService } from "./analytics";
import type { PreviewEventWithMetadata } from "./caseStore";
import { useCaseStore } from "./caseStore";

export const G_BUTTON_SOURCE = "tab_bar_granted_button";
export const GET_STARTED_SOURCE = "home_getting_started_widget";
export const CASES_LIST_NEW_CASE_BUTTON_SOURCE = "cases_list_new_case_button";
export const WEB_SIDEBAR_ASK_GRANTED_SOURCE = "web_sidebar_ask_granted";

export const usePushGetHelp = () => {
  const { push } = useRouter();
  const setPreviewData = useCaseStore(state => state.setPreviewData);

  const pushGetHelp = ({
    caseType,
    title,
    source,
  }: {
    caseType:
      | "BENEFITS_QUESTION"
      | "OTHER"
      | "OPEN_ENROLLMENT"
      | "BILL_ISSUE"
      | "BILL_ISSUE_V2";
    title: string;
    source: string;
  }) => {
    void HapticsService.impactMedium();
    analyticsService.trackEvent("CaseIntakeStarted", {
      source,
    });

    let messageId: GetHelpPromptKey = `get_help_prompt_${caseType}`;
    if (
      [
        G_BUTTON_SOURCE,
        GET_STARTED_SOURCE,
        CASES_LIST_NEW_CASE_BUTTON_SOURCE,
        WEB_SIDEBAR_ASK_GRANTED_SOURCE,
      ].includes(source)
    ) {
      messageId = "get_help_prompt_get_started";
    }
    const message = GET_HELP_PROMPT_CASE_TYPE_MAP[messageId];

    // Generate storeKey and create preview data
    const storeKey = uuid();
    const events: PreviewEventWithMetadata[] = [];

    events.push({
      event: createSystemMessageEvent(message.message, message.options),
      metadata: { messageId },
    });

    if (caseType === "BILL_ISSUE_V2") {
      events.push({ event: createDocumentUploadEvent(), metadata: {} });
    }

    // Save data to store
    setPreviewData(storeKey, { events });

    // Navigate to preview case
    push(
      issuePreviewUrl({
        // TODO: remove the ternary once we release this to production and we
        // don't have to support old versions of the app.
        // https://linear.app/medbillai/issue/GRO-117/remove-bill-upload-ui-feature-flag
        caseType: caseType === "BILL_ISSUE_V2" ? "BILL_ISSUE" : caseType,
        title,
        storeKey,
      }),
    );
  };

  return { pushGetHelp };
};
